import { RequestError } from "../requestError";
import api from "../axiosBase";
import { getBetaURL, getURL } from "../serviceHelper";
import { getEnv } from "../../AppConfigs";
import { isEqualBetweenSharepointUserAndLoginUser } from "../util";
import {
  ISharepointPermissionRoles,
  ISharepointCreateLinkRequest,
  ISharepointUser,
  ISharepointLink,
  ISharepointLinkInfo,
} from "../../models/ISharepoint";
import { SharePointLinkType, SharePointPermissionRole, SharePointScope } from "../../constants/sharePointConstant";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const DefaultSPListId = getEnv("REACT_APP_FOLDER_AND_FILE_SP_LIST_ID");

export class PermissionService {
  static async checkWritePermission(user: any, id: any, listId?: string, isFolder?: boolean, isMasterUser?: boolean) {
    if (isMasterUser) return true;

    const result = await this.getDocumentPermissons(id, listId, isFolder);
    if (!result || result.count.length === 0) return true;

    if (
      result.data.find((p: ISharepointLink) => {
        return (
          p.link.type === SharePointLinkType.edit &&
          p.users &&
          p.users.find((u: ISharepointUser) => isEqualBetweenSharepointUserAndLoginUser(u, user))
        );
      })
    )
      return true;
    //check if user is read

    return false;
  }

  static async updateDocumentPermissons(id: any, emails: string[], roles: string[], listId?: string) {
    if (!listId) listId = DefaultSPListId;

    if (emails?.length === 0) return null;

    const recipients = emails.map((email: any) => {
      return { email };
    });

    const fields = {
      requireSignIn: true,
      sendInvitation: true,
      roles: roles,
      recipients: recipients,
    };

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root/invite`;
    if (id) url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}/invite`;

    return api
      .post(getURL(url), fields)
      .then((res) => {
        return res.data.value.map((v: any) => this.mapShareLinkFromPermission(v));
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async createSharedLink(
    id: any,
    isDocumentLibrary: boolean,
    scope: string,
    type: string,
    emails?: string[],
    listId?: string
  ) {
    if (!listId) listId = DefaultSPListId;

    let recipients: any = undefined;
    if (emails) {
      recipients = emails.map((email: any) => {
        return { email };
      });
    }

    const fields: ISharepointCreateLinkRequest = {
      sendInvitation: true,
      type: type,
      scope: scope,
      recipients: recipients,
    };

    let itemPath = `items/${id}`;
    if (isDocumentLibrary) {
      itemPath = !id || id === "root" ? `drive/root` : `drive/items/${id}`;
    }

    let url = `/sites/${SPSiteId}/lists/${listId}/${itemPath}/createLink`;

    return api
      .post(getBetaURL(url), fields)
      .then((res) => this.mapSharepointLink(res.data))
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async revokeSharedLink(
    id: any,
    isDocumentLibrary: boolean,
    permissionId: string,
    emails: string,
    listId?: string
  ) {
    if (!listId) listId = DefaultSPListId;

    const fields = {
      grantees: [{ email: emails }],
    };

    let itemPath = `items/${id}`;
    if (isDocumentLibrary) {
      itemPath = !id || id === "root" ? `drive/root` : `drive/items/${id}`;
    }

    let url = `/sites/${SPSiteId}/lists/${listId}/${itemPath}/permissions/${permissionId}/revokeGrants`;

    return api
      .post(getBetaURL(url), fields)
      .then((res) => this.mapSharepointLink(res.data))
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getDocumentPermissons(id: any, listId?: string, isFolder?: boolean) {
    if (!id && !isFolder) throw new Error("Tài liệu không hợp lệ");

    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root/permissions`;
    if (id) url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}/permissions`;

    return api
      .get(getBetaURL(url))
      .then((res) => {
        const child = res.data.value
          .map((item: any) => this.mapShareLinkFromPermission(item))
          .filter((l: ISharepointLink) => l.users?.length > 0);

        return {
          data: child,
          count: child.length,
        };
      })
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static async getDocumentPermissonsV2(id: any, listId?: string, isFolder?: boolean) {
    if (!id && !isFolder) throw new Error("Tài liệu không hợp lệ");

    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root/permissions`;
    if (id) url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}/permissions`;

    const res = await api.get(getBetaURL(url));
    return res.data.value;
  }

  static async deleteDocumentPermission(id: any, permissionId: string, listId?: string, isFolder?: boolean) {
    if (!id && !isFolder) throw new Error("Tài liệu không hợp lệ");

    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root/permissions/${permissionId}`;
    if (id) url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}/permissions/${permissionId}`;

    return api
      .delete(getBetaURL(url))
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.log(err);
      });
  }

  static async deletePermission(id: any, permissionId: string, listId?: string) {
    if (!listId) listId = DefaultSPListId;

    let url = `/sites/${SPSiteId}/lists/${listId}/drive/root/permissions/${permissionId}`;
    if (id) url = `/sites/${SPSiteId}/lists/${listId}/drive/items/${id}/permissions/${permissionId}`;

    return api
      .delete(getURL(url))
      .then((res) => res.data)
      .catch((err) => {
        throw new RequestError(err);
      });
  }

  static mapSharepointLink(data: any) {
    var result: ISharepointLink = {
      id: data.id,
      hasPassword: data.hasPassword,
      roles: data.roles,
      link: data.link,
      expirationDateTime: data.expirationDateTime ? new Date(data.expirationDateTime) : undefined,
      users: data.grantedToIdentitiesV2?.map((p: any) => this.mapGrandedUserToSharepointUser(p)),
    };

    return result;
  }

  static mapGrandedUserToSharepointUser = (item: any) => {
    if (!item) return undefined;

    if (item.group) {
      const user = item.group as ISharepointUser;
      user.isGroup = true;
      return user;
    } else {
      return item.user as ISharepointUser;
    }
  };

  static mapPermissionRoles(permissions: Array<ISharepointLink>) {
    let readPermissions: Array<ISharepointUser> = [];
    let writePermissions: Array<ISharepointUser> = [];
    permissions.forEach((item: any) => {
      if (item.roles?.length > 0 && item.users && item.users.length > 0) {
        item.roles.forEach((role: string) => {
          const emailUsers = item.users.filter((u: any) => u.email);
          if (role === SharePointPermissionRole.read) {
            readPermissions.push(emailUsers);
          } else {
            writePermissions.push(emailUsers);
          }
        });
      }
    });

    const result: ISharepointPermissionRoles = {
      read: readPermissions,
      write: writePermissions,
    };
    return result;
  }

  static mapShareLinkFromPermission(item: any) {
    let users: Array<ISharepointUser> = [];
    const link: ISharepointLinkInfo = item.link ?? {
      type: item.roles.find((r: any) => r === SharePointPermissionRole.read)
        ? SharePointLinkType.view
        : SharePointLinkType.edit,
      webUrl: "",
      scope: SharePointScope.users,
      preventsDownload: false,
    };

    if (item.grantedToV2 || item.grantedTo) {
      const user = this.mapGrandedUserToSharepointUser(item.grantedToV2 ?? item.grantedTo);
      if (user) users.push(user);
    } else if (item.grantedToIdentitiesV2) {
      users = item.grantedToIdentitiesV2.map((p: any) => this.mapGrandedUserToSharepointUser(p));
    }

    const result: ISharepointLink = {
      id: item.id,
      roles: item.roles,
      users: users,
      hasPassword: item.hasPassword ?? false,
      link: link,
    };

    return result;
  }
}
